import { LocalizedLink as Link } from './LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';

/** URL do app na Play Store — reutilizada no rodapé e na seção CTA da home. */
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bolaoelite.app';

/** Badge "Disponível no Google Play" — botão estilizado (não a imagem oficial,
 *  que tem restrições de branding). Ícone simples do Play + texto. */
export function PlayStoreBadge({ className = '' }: { className?: string }) {
  const { t } = useTranslation('common');
  return (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2.5 bg-black hover:bg-gray-800 text-white rounded-lg px-4 py-2.5 transition-colors ${className}`}
    >
      {/* Ícone do Google Play (triângulo colorido) */}
      <svg viewBox="0 0 512 512" className="w-6 h-6 flex-shrink-0" aria-hidden="true">
        <path fill="#00D2FF" d="M47 16.5C40.6 22.2 37 31 37 42v428c0 11 3.6 19.8 10 25.5l1.4 1.3 239.7-239.7v-2.7L48.4 15.2 47 16.5z" />
        <path fill="#FFCE00" d="M368 336.7l-79.9-80v-2.7l80-80 1.8 1L466 230.6c27.1 15.4 27.1 40.6 0 56L370 343l-2 -.3-.4-6z" transform="translate(-1 0)" />
        <path fill="#00F076" d="M369.4 335.7L287.7 254 47 494.8c8.9 9.5 23.7 10.6 40.3 1.2L369.4 335.7z" />
        <path fill="#FF3A44" d="M369.4 172.3L87.3 16C70.7 6.7 55.9 7.7 47 17.2L287.7 254l81.7-81.7z" />
      </svg>
      <span className="flex flex-col leading-tight text-left">
        <span className="text-[10px] opacity-80">{t('footer.play_available', 'Disponível no')}</span>
        <span className="text-sm font-semibold -mt-0.5">Google Play</span>
      </span>
    </a>
  );
}

const LINK_CLASS = 'text-sm text-gray-500 hover:text-gray-300 transition-colors';

/** Rodapé compartilhado para todas as landing pages públicas (área não logada).
 *  Estrutura: 4 colunas de links (Produto, Conteúdo, Empresa, Legal) +
 *  disclaimer regulatório + bottom bar com copyright e Play Store. */
export function PublicFooter() {
  const { t } = useTranslation('common');
  return (
    <footer className="bg-gray-900 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* 4 colunas: Produto | Conteúdo | Empresa | Legal */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 pb-10 max-w-5xl mx-auto">
          {/* Coluna 1 — Produto */}
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('footer.col_product', 'Produto')}</p>
            <ul className="flex flex-col gap-2">
              <li><Link to="/premium" className={LINK_CLASS}>{t('link.premium', 'Premium')}</Link></li>
              <li><Link to="/precos" className={LINK_CLASS}>{t('link.pricing', 'Preços')}</Link></li>
              <li><Link to="/simulador-copa-do-mundo-2026" className={LINK_CLASS}>{t('link.sim_world_cup', 'Simulador Copa')}</Link></li>
              <li><Link to="/simulador-libertadores-2026" className={LINK_CLASS}>{t('link.sim_libertadores', 'Simulador Libertadores')}</Link></li>
              <li><Link to="/simulador-copa-sudamericana-2026" className={LINK_CLASS}>{t('link.sim_sudamericana', 'Simulador Sul-Americana')}</Link></li>
              <li><Link to="/escalar-selecao-brasileira-2026" className={LINK_CLASS}>{t('link.squad_builder', 'Escalar Seleção')}</Link></li>
              <li><Link to="/escale-seu-time" className={LINK_CLASS}>{t('link.team_builder', 'Escale Seu Time')}</Link></li>
            </ul>
          </div>

          {/* Coluna 2 — Conteúdo */}
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('footer.col_content', 'Conteúdo')}</p>
            <ul className="flex flex-col gap-2">
              <li><Link to="/blog" className={LINK_CLASS}>{t('link.blog', 'Blog')}</Link></li>
              <li><Link to="/como-jogar" className={LINK_CLASS}>{t('link.how_to_play', 'Como Jogar')}</Link></li>
              <li><Link to="/glossario" className={LINK_CLASS}>{t('link.glossary', 'Glossário')}</Link></li>
              <li><Link to="/bolao-copa-do-mundo" className={LINK_CLASS}>{t('link.pool_world_cup', 'Bolão Copa do Mundo')}</Link></li>
              <li><Link to="/bolao-brasileirao" className={LINK_CLASS}>{t('link.pool_brasileirao', 'Bolão Brasileirão')}</Link></li>
            </ul>
          </div>

          {/* Coluna 3 — Empresa */}
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('footer.col_company', 'Empresa')}</p>
            <ul className="flex flex-col gap-2">
              <li><Link to="/sobre" className={LINK_CLASS}>{t('link.about', 'Sobre nós')}</Link></li>
              <li><Link to="/contato" className={LINK_CLASS}>{t('link.contact', 'Contato')}</Link></li>
              <li>
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                  {t('link.download_app', 'Baixar app')}
                </a>
              </li>
            </ul>
          </div>

          {/* Coluna 4 — Legal */}
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('footer.col_legal', 'Legal')}</p>
            <ul className="flex flex-col gap-2">
              <li><Link to="/termos-de-uso" className={LINK_CLASS}>{t('link.terms', 'Termos de Uso')}</Link></li>
              <li><Link to="/privacidade" className={LINK_CLASS}>{t('link.privacy', 'Privacidade')}</Link></li>
              <li><Link to="/regulamento" className={LINK_CLASS}>{t('link.rules', 'Regulamento')}</Link></li>
              <li><Link to="/excluir-conta" className={LINK_CLASS}>{t('link.delete_account', 'Excluir conta')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Disclaimer regulatório — reforça que NÃO é casa de apostas */}
        <div className="border-t border-gray-800 pt-6 pb-4 max-w-3xl mx-auto text-center">
          <p className="text-xs text-gray-500 leading-relaxed">
            <Trans i18nKey="footer.disclaimer" t={t}>
              Bolão Elite é uma plataforma recreativa de palpites de futebol entre
              amigos. <strong className="text-gray-400">Não envolve apostas com
              dinheiro real.</strong> Quando há premiação, ela segue a
              modalidade de concurso cultural prevista na Lei 5.768/71 e no
              Decreto 70.951/72, com participação 100% gratuita e resultado
              dependente exclusivamente da habilidade do usuário.
            </Trans>
          </p>
        </div>

        {/* Bottom bar: copyright + Play Store badge */}
        <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-800 pt-5 gap-4">
          <p className="text-xs text-gray-500 text-center sm:text-left">
            {t('footer.copyright', {
              year: new Date().getFullYear(),
              defaultValue: 'Copyright © {{year}} · Todos os direitos reservados · Bolão Elite',
            })}
          </p>
          <PlayStoreBadge />
        </div>
      </div>
    </footer>
  );
}
